import { AppShell, Spinner } from "@design";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";
import { ApiError } from "@/api";
import { useAuth } from "@/auth/AuthContext";
import { DevPersonaBanner } from "@/auth/dev/DevPersonaBanner";
import { CenteredScreen } from "@/components/CenteredScreen";
import { AboutProvider } from "@/features/about/AboutProvider";
import { BlockedScreen } from "@/features/account/BlockedScreen";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { ConnectorSignInResult } from "@/features/build/connectors/useConnectorSignIn";
import { FeedbackProvider } from "@/features/feedback/FeedbackProvider";
import { DesktopPermissionBanner } from "@/features/notifications/DesktopPermissionBanner";
import { NotificationsProvider } from "@/features/notifications/NotificationsProvider";
import { useViewport } from "@/responsive/viewport";
import { TelemetryUser } from "@/telemetry/TelemetryUser";
import { ThemeSync } from "@/theme/ThemeSync";
import { AppSidebar } from "./AppSidebar";
import { CommandPaletteProvider } from "./commandPalette/CommandPaletteProvider";
import { MobileShell } from "./MobileShell";
import { SignInScreen } from "./SignInScreen";
import { BudgetBanner } from "./usage/BudgetBanner";

function LoadingScreen({ label }: Readonly<{ label: string }>) {
  return (
    <CenteredScreen>
      <Spinner size="lg" aria-label={label} />
    </CenteredScreen>
  );
}

/**
 * Root layout for every route. Gates in order:
 *   1. auth resolving → spinner
 *   2. signed out     → sign-in (MSAL button or dev persona picker)
 *   3. profile load   → spinner; platform block (403) → BlockedScreen
 *   4. app shell
 */
export function AppLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation("shell");
  if (isLoading) return <LoadingScreen label={t("loading.signingIn")} />;
  if (!isAuthenticated) return <SignInScreen />;
  return <ProfileGate />;
}

function ProfileGate() {
  const me = useCurrentUser();
  const { t } = useTranslation("shell");
  if (me.isPending) return <LoadingScreen label={t("loading.profile")} />;
  if (me.error instanceof ApiError && me.error.platformBlock) return <BlockedScreen error={me.error} />;
  // Other profile errors (backend down etc.) don't block the shell; pages show their own errors.
  return <Shell />;
}

const renderMobileNav = (close: () => void) => <AppSidebar onClose={close} />;

/** Desktop/tablet: design-system AppShell. Mobile: temporary hamburger shell. */
function Shell() {
  const { isMobile } = useViewport();
  const content = (
    <>
      <ThemeSync />
      <TelemetryUser />
      <ConnectorSignInResult />
      <Outlet />
    </>
  );
  const banners = (
    <>
      <DevPersonaBanner />
      <BudgetBanner />
      <DesktopPermissionBanner />
    </>
  );
  return (
    // Feedback (and About) wrap the palette so the palette's "Send feedback" action can open it.
    <AboutProvider>
      <FeedbackProvider>
        <CommandPaletteProvider>
          <NotificationsProvider>
            {isMobile ? (
              <MobileShell banner={banners} nav={renderMobileNav}>
                {content}
              </MobileShell>
            ) : (
              <AppShell banner={banners} nav={<AppSidebar />}>
                <div className="h-full overflow-y-auto">{content}</div>
              </AppShell>
            )}
          </NotificationsProvider>
        </CommandPaletteProvider>
      </FeedbackProvider>
    </AboutProvider>
  );
}
